/**
 * 複数IDの一括取得を安全に行うための純粋関数群。
 * （リポジトリ実装から切り離してあるので、Node環境でもテストできる）
 *
 * 本番APIでは数万件の保存IDを扱い得るため、
 *  - 1リクエストあたりのID数を制限する
 *  - 一部のチャンクが失敗しても他の取得結果は捨てない
 *  - 元の保存順を維持する
 *  - 重複IDを除去する
 *  - 取得できないIDは missingIds として返す（保存データは消さない）
 */

export const DEFAULT_BATCH_SIZE = 20;
export const MAX_BATCH_SIZE = 50;
export const MAX_CONCURRENT_BATCHES = 2;

/** 空・空白を除去し、大小文字を無視して重複を落とす（元の順序は維持） */
export function normalizeIds(ids: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  ids.forEach((raw) => {
    if (typeof raw !== "string") return;
    const value = raw.trim();
    if (!value) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(value);
  });
  return result;
}

export function chunkIds(ids: string[], size: number = DEFAULT_BATCH_SIZE): string[][] {
  const safeSize = Math.min(Math.max(1, Math.floor(size) || DEFAULT_BATCH_SIZE), MAX_BATCH_SIZE);
  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += safeSize) {
    chunks.push(ids.slice(index, index + safeSize));
  }
  return chunks;
}

/** 同時実行数を絞って順番に処理する（大量IDでも同時リクエストを増やさない） */
export async function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;
  const safeLimit = Math.max(1, Math.min(limit, items.length || 1));

  async function next(): Promise<void> {
    const index = cursor;
    cursor += 1;
    if (index >= items.length) return;
    results[index] = await worker(items[index], index);
    await next();
  }

  await Promise.all(Array.from({ length: safeLimit }, () => next()));
  return results;
}

export interface ChunkResult<T> {
  /** 取得成功した作品 */
  items: T[];
  /** 取得できなかったID（＝削除・販売終了など） */
  missingIds: string[];
}

export interface MergedBatchResult<T> {
  /** 依頼順（重複除去後）に並べた作品 */
  items: T[];
  /** 存在しなかったID */
  missingIds: string[];
  /** 通信に失敗したID（存在しないとは限らない） */
  unresolvedIds: string[];
  /** 一部でも通信に失敗したか */
  failed: boolean;
}

/**
 * チャンクごとの結果を、依頼した順序で1つにまとめる。
 * results の null はそのチャンクが通信エラーだったことを示す。
 */
export function mergeBatchResults<T>(
  uniqueIds: string[],
  results: (ChunkResult<T> | null)[],
  getKeys: (item: T) => string[],
): MergedBatchResult<T> {
  const byKey = new Map<string, T>();
  const found = new Set<string>();
  const failed = results.some((result) => result === null);

  results.forEach((result) => {
    if (!result) return;
    result.items.forEach((item) => {
      getKeys(item).forEach((key) => {
        if (!key) return;
        const normalized = key.toLowerCase();
        if (!byKey.has(normalized)) byKey.set(normalized, item);
      });
    });
    result.missingIds.forEach((id) => {
      if (typeof id === "string") found.add(id.trim().toLowerCase());
    });
  });

  const items: T[] = [];
  const missingIds: string[] = [];
  uniqueIds.forEach((id) => {
    const key = id.toLowerCase();
    const item = byKey.get(key);
    if (item) {
      if (!items.includes(item)) items.push(item);
      return;
    }
    if (found.has(key) || !failed) missingIds.push(id);
  });

  return { items, missingIds, unresolvedIds: failed ? uniqueIds.filter((id) => !byKey.has(id.toLowerCase()) && !found.has(id.toLowerCase())) : [], failed };
}